import React, { useMemo, useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Cpu, HardDrive, X, Zap, Plus } from "lucide-react";
import { toast } from "sonner";

// A "cyberdeck" is any cybertech item where is_deck=true (bought from shop)
// or, for legacy hand-typed items, any text containing "cyberdeck".
export function isDeck(it) {
  if (!it) return false;
  if (it.is_deck) return true;
  return /cyberdeck/i.test(it.text || "");
}
export function slotsFor(deck) {
  if (typeof deck.slots === "number") return deck.slots;
  return /\+/.test(deck.text || "") ? 6 : 3;
}

// Ensure every app / cybertech item has a stable id (mutates a copy).
function withIds(arr) {
  return (arr || []).map((it, i) => ({ ...it, id: it.id || `it_${i}_${Math.random().toString(36).slice(2, 8)}` }));
}

export default function CyberdeckLoadout({ character, onChange }) {
  const [pickerFor, setPickerFor] = useState(null); // deck.id when picking an app
  const [bootingId, setBootingId] = useState(null);
  const idPatchedRef = useRef(false);

  const decksWithIds = useMemo(() => withIds(character.cybertech).filter(isDeck), [character.cybertech]);
  const appsWithIds = useMemo(() => withIds(character.apps), [character.apps]);

  // One-time: persist auto-generated ids back to character so future actions align.
  useEffect(() => {
    if (idPatchedRef.current) return;
    const cyberNeedsId = (character.cybertech || []).some((it) => !it.id);
    const appsNeedId = (character.apps || []).some((it) => !it.id);
    if (cyberNeedsId || appsNeedId) {
      idPatchedRef.current = true;
      onChange({ cybertech: withIds(character.cybertech), apps: withIds(character.apps) });
    }
  }, [character, onChange]);

  const appsOn = (deckId) => appsWithIds.filter((a) => a.loaded_to === deckId);
  const unloaded = appsWithIds.filter((a) => !a.loaded_to);

  const setApps = (next) => onChange({ apps: next });

  const loadApp = (appId, deckId) => {
    const deck = decksWithIds.find((d) => d.id === deckId);
    if (!deck) return;
    if (appsOn(deckId).length >= slotsFor(deck)) {
      toast.error("DECK FULL", { description: "No free slots on this cyberdeck." });
      return;
    }
    const next = appsWithIds.map((a) => (a.id === appId ? { ...a, loaded_to: deckId } : a));
    setApps(next);
    setBootingId(appId);
    setTimeout(() => setBootingId(null), 700);
    const app = appsWithIds.find((a) => a.id === appId);
    toast.success(`APP LOADED · ${app?.text || "app"}`, { description: `Running on ${deck.text}` });
    setPickerFor(null);
  };

  const unloadApp = (appId) => {
    const app = appsWithIds.find((a) => a.id === appId);
    setApps(appsWithIds.map((a) => (a.id === appId ? { ...a, loaded_to: null } : a)));
    toast(`APP UNLOADED · ${app?.text || "app"}`, { description: "Returned to buffer." });
  };

  if (decksWithIds.length === 0) {
    return (
      <div className="panel panel-cyan p-4 mb-4" data-testid="cyberdeck-empty">
        <div className="flex items-center gap-2 mb-2">
          <HardDrive className="text-[color:var(--accent-cyan)]" size={18} />
          <div className="font-display uppercase font-black tracking-tight text-lg">CYBERDECK LOADOUT</div>
          <span className="tape tape-magenta text-[10px] ml-auto">OFFLINE</span>
        </div>
        <div className="font-mono text-xs text-[color:var(--text-muted)] uppercase tracking-widest">
          No cyberdeck installed. Buy one from the BLACK MARKET → CYBERTECH tab to run apps.
        </div>
      </div>
    );
  }

  return (
    <div className="panel panel-cyan p-4 mb-4 relative" data-testid="cyberdeck-loadout">
      <div className="flex items-center gap-2 mb-3">
        <Cpu className="text-[color:var(--accent-cyan)]" size={18} />
        <div className="font-display uppercase font-black tracking-tight text-lg">CYBERDECK LOADOUT</div>
        <span className="tape tape-cyan text-[10px] ml-auto">
          {appsWithIds.filter((a) => a.loaded_to).length} / {decksWithIds.reduce((n, d) => n + slotsFor(d), 0)} SLOTS USED
        </span>
      </div>

      {/* Decks */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {decksWithIds.map((deck) => {
          const loaded = appsOn(deck.id);
          const slots = slotsFor(deck);
          const cells = Array.from({ length: slots }, (_, i) => loaded[i] || null);
          return (
            <div key={deck.id} className="border-2 border-[color:var(--accent-cyan)] p-3" data-testid={`deck-${deck.id}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="font-display uppercase font-black text-sm text-[color:var(--accent-cyan)] truncate">{deck.text}</div>
                <span className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--text-muted)]">
                  {loaded.length}/{slots}
                </span>
              </div>
              <div className={`grid ${slots > 3 ? "grid-cols-3" : "grid-cols-3"} gap-2`}>
                {cells.map((app, i) => (
                  <div key={i} className="relative">
                    {app ? (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.25 }}
                        className={`border-2 border-[color:var(--accent-yellow)] bg-[color:var(--bg-panel-2)] p-2 min-h-[64px] flex flex-col justify-between text-left ${bootingId === app.id ? "result-flash" : ""}`}
                        data-testid={`slot-${deck.id}-${i}`}
                      >
                        <div className="flex items-start justify-between gap-1">
                          <div className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--accent-yellow)] leading-tight break-words">
                            {bootingId === app.id ? "BOOTING…" : app.text}
                          </div>
                          <button
                            aria-label="Unload"
                            data-testid={`unload-${app.id}`}
                            onClick={() => unloadApp(app.id)}
                            className="text-[color:var(--text-muted)] hover:text-[color:var(--accent-magenta)] shrink-0"
                          >
                            <X size={12} />
                          </button>
                        </div>
                        <div className="text-[9px] font-mono text-[color:var(--text-muted)] uppercase">SLOT {i + 1} · ACTIVE</div>
                      </motion.div>
                    ) : (
                      <button
                        data-testid={`slot-${deck.id}-${i}-empty`}
                        onClick={() => unloaded.length ? setPickerFor(deck.id) : toast("No unloaded apps available", { description: "Buy or add an app first." })}
                        className="border-2 border-dashed border-[color:var(--border-hard)] hover:border-[color:var(--accent-cyan)] p-2 min-h-[64px] w-full flex flex-col items-center justify-center gap-1 text-[color:var(--text-muted)] hover:text-[color:var(--accent-cyan)]"
                      >
                        <Plus size={16} />
                        <span className="text-[9px] font-mono uppercase tracking-widest">SLOT {i + 1}</span>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Unloaded pool */}
      <div className="mt-4 border-t-2 border-dashed border-[color:var(--border-hard)] pt-3">
        <div className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--text-secondary)] mb-2 flex items-center gap-1">
          <Zap size={12} className="text-[color:var(--accent-magenta)]" /> UNLOADED APPS
          <span className="ml-auto text-[color:var(--text-muted)]">{unloaded.length}</span>
        </div>
        {unloaded.length === 0 ? (
          <div className="font-mono text-xs text-[color:var(--text-muted)]">— every app is running.</div>
        ) : (
          <div className="flex flex-wrap gap-2" data-testid="unloaded-pool">
            {unloaded.map((a) => (
              <button
                key={a.id}
                data-testid={`unloaded-${a.id}`}
                onClick={() => {
                  // auto-load into first deck with a free slot
                  const target = decksWithIds.find((d) => appsOn(d.id).length < slotsFor(d));
                  if (!target) return toast.error("ALL DECKS FULL");
                  loadApp(a.id, target.id);
                }}
                className="border-2 border-[color:var(--accent-magenta)] text-[color:var(--accent-magenta)] hover:bg-[color:var(--accent-magenta)] hover:text-[color:var(--bg-void)] px-2 py-1 text-xs font-mono uppercase tracking-widest"
                title="Click to load into first available slot"
              >
                {a.text}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Picker overlay when a specific slot is chosen */}
      <AnimatePresence>
        {pickerFor && (
          <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/80 backdrop-blur-sm"
              onClick={() => setPickerFor(null)}
            />
            <motion.div
              initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
              className="relative panel panel-cyan p-4 w-full max-w-md"
              data-testid="loader-picker"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="font-display uppercase font-black tracking-tight text-lg">
                  LOAD APP → {decksWithIds.find((d) => d.id === pickerFor)?.text}
                </div>
                <button data-testid="loader-picker-close" onClick={() => setPickerFor(null)} aria-label="Close" className="p-1 hover:text-[color:var(--accent-magenta)]">
                  <X size={16} />
                </button>
              </div>
              {unloaded.length === 0 ? (
                <div className="font-mono text-xs text-[color:var(--text-muted)]">No apps available. Add or buy some.</div>
              ) : (
                <div className="flex flex-col gap-2 max-h-[50vh] overflow-y-auto">
                  {unloaded.map((a) => (
                    <button
                      key={a.id}
                      data-testid={`pick-${a.id}`}
                      onClick={() => loadApp(a.id, pickerFor)}
                      className="cy-btn cy-btn-cyan text-xs justify-start"
                    >
                      {a.text}
                    </button>
                  ))}
                </div>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
